import { AlertOutlined, BuildOutlined, PlusSquareOutlined, DeleteOutlined } from '@ant-design/icons';
import { Button, Empty, Form, Input, Modal, Typography, Tabs, Card, message, Spin } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { AppDispatch } from 'src/index';
import styles from 'src/styles/layout.module.css';
import { useForm } from 'antd/es/form/Form';

import { createPackage, fetchPackages } from 'src/features/companyPackages/companyPackages.state';
import { usePackagesValues } from 'src/features/companyPackages/companyPackages.hooks';
import PackageTile from 'src/components/atoms/PackageTile';
import { DraftCompanyPackage } from 'src/features/companyPackages/companyPackages.types';

import { getPaymentTexts, savePaymentTexts, deletePaymentText } from 'src/features/companyPayText/companyPayText.api';

import { RoleEnum } from 'src/features/users/users.types';
import ComponentRoleProtected from 'src/components/blocks/ComponentRoleProtected';

const SUPPORTED_LANGS = ['ru', 'en'];

type NewPackage = Omit<DraftCompanyPackage, 'vipAccess' | 'promo' | 'isKids'>;
const emptyPackage: NewPackage & {
  name: string;
} = {
  name: '',
  description: '',
  duration: 0,
  price: 0,
};

interface PaymentTextFormValues {
  translations: Record<string, string>;
}

const CompanyPackagesTab: React.FC = () => {
  const { companyId } = useParams() as { companyId: string };
  const dispatch: AppDispatch = useDispatch();
  const intl = useIntl();
  const createAction = createPackage;
  const entityValues = usePackagesValues();
  const [packageForm] = useForm();
  const [createMode, setCreateMode] = useState<boolean>(false);
  const [newEntity, setNewEntity] = useState<NewPackage & { name: string }>(emptyPackage);

  const handlePackageSubmit = useCallback(async () => {
    const response = await dispatch(createAction({ companyId, data: newEntity }));
    if (createAction.fulfilled.match(response)) {
      setNewEntity(emptyPackage);
      setCreateMode(false);
    }
    if (createAction.rejected.match(response)) {
      Modal.error({
        title: intl.formatMessage({ id: response.payload?.code }),
        icon: <AlertOutlined />,
      });
    }
  }, [companyId, createAction, dispatch, intl, newEntity]);

  const fetchInitialPackages = useCallback(async () => {
    const response = await dispatch(fetchPackages({ companyId }));
    if (fetchPackages.rejected.match(response)) {
      Modal.error({
        title: intl.formatMessage({ id: 'failedToRetrieveDataFromServer' }),
        icon: <Empty />,
      });
    }
  }, [dispatch, intl, companyId]);

  const handleCreatePackageClick = useCallback(() => {
    setCreateMode(true);
  }, []);

  useEffect(() => {
    fetchInitialPackages();
  }, [fetchInitialPackages]);

  const [paymentTextForm] = useForm<PaymentTextFormValues>();
  const [loadingTexts, setLoadingTexts] = useState(false);
  const [savingTexts, setSavingTexts] = useState(false);
  const [activeLang, setActiveLang] = useState<string>('ru');
  const [currentTranslations, setCurrentTranslations] = useState<Record<string, string>>({});

  const fetchPaymentTexts = useCallback(async () => {
    console.log('--- START FETCH PAYMENT TEXTS ---');
    setLoadingTexts(true);
    try {
      const res = await getPaymentTexts({ companyId });

      if (!res.data || !Array.isArray(res.data.translations)) {
        console.error('CRITICAL: Invalid response structure', res.data);
        message.error('Ошибка структуры данных от сервера');
        return;
      }

      const translationsList = res.data.translations;

      const nextValues: Record<string, string> = {};
      SUPPORTED_LANGS.forEach(lang => {
        nextValues[lang] = '';
      });

      translationsList.forEach((t: any) => {
        if (SUPPORTED_LANGS.includes(t.lang)) {
          const rawText = t.text_after_payment || t.textAfterPayment || t.text;
          const val = rawText && rawText !== 'null' ? String(rawText) : '';
          nextValues[t.lang] = val;
        }
      });

      setCurrentTranslations(nextValues);

      setTimeout(() => {
        paymentTextForm.setFieldsValue({
          translations: nextValues,
        });
      }, 100);
    } catch (error) {
      console.error('Fetch Error:', error);
      message.error('Ошибка загрузки текстов');
    } finally {
      setLoadingTexts(false);
    }
  }, [companyId, paymentTextForm]);

  useEffect(() => {
    fetchPaymentTexts();
  }, [fetchPaymentTexts]);

  const handleClearLang = useCallback(
    (lang: string) => {
      Modal.confirm({
        title: `Очистить текст для ${lang.toUpperCase()}?`,
        content: 'Это действие удалит сохраненный текст для этого языка.',
        okText: 'Очистить',
        okType: 'danger',
        cancelText: 'Отмена',
        onOk: async () => {
          const currentVals = paymentTextForm.getFieldsValue().translations || {};
          const newVals = { ...currentVals, [lang]: '' };

          paymentTextForm.setFieldsValue({
            translations: newVals,
          });

          setCurrentTranslations(newVals);

          const payload: Record<string, string | null> = {};
          SUPPORTED_LANGS.forEach(l => {
            payload[l] = l === lang ? null : newVals[l] || null;
          });

          try {
            setSavingTexts(true);
            await savePaymentTexts({ companyId, data: payload });
            message.success(`Текст для ${lang.toUpperCase()} очищен`);
          } catch (e) {
            message.error('Ошибка при очистке');
          } finally {
            setSavingTexts(false);
          }
        },
      });
    },
    [companyId, paymentTextForm]
  );

  const handleSaveTexts = useCallback(async () => {
    try {
      const values = await paymentTextForm.validateFields();
      const allTranslations = values.translations;

      const payload: Record<string, string | null> = {};
      let hasAnyContent = false;

      Object.keys(allTranslations).forEach(lang => {
        const text = allTranslations[lang];
        if (text && text.trim().length > 0) {
          hasAnyContent = true;
        }
        payload[lang] = text && text.trim().length > 0 ? text : null;
      });

      if (!hasAnyContent) {
        message.warning('Заполните текст хотя бы для одного языка');
        return;
      }

      setSavingTexts(true);
      await savePaymentTexts({ companyId, data: payload });

      message.success('Тексты сохранены');
      await fetchPaymentTexts();
    } catch (error: any) {
      if (error?.errorFields) {
        message.error('Проверьте правильность заполнения полей');
      } else {
        console.error(error);
        message.error('Ошибка сохранения');
      }
    } finally {
      setSavingTexts(false);
    }
  }, [companyId, paymentTextForm, fetchPaymentTexts]);

  const handleTabChange = (key: string) => {
    setActiveLang(key);
  };

  return (
    <div className={styles.root}>
      <ComponentRoleProtected roles={[RoleEnum.admin, RoleEnum.companyContentManager]}>
        <Card
          title='Текст после оплаты'
          extra={
            <Button type='primary' icon={<SaveOutlined />} onClick={handleSaveTexts} loading={savingTexts}>
              Сохранить
            </Button>
          }
          style={{ marginBottom: 24 }}
        >
          {loadingTexts ? (
            <Spin />
          ) : (
            <Form form={paymentTextForm} layout='vertical'>
              <Tabs activeKey={activeLang} onChange={handleTabChange} style={{ marginBottom: 0 }}>
                {SUPPORTED_LANGS.map(lang => {
                  const hasText = currentTranslations[lang] && currentTranslations[lang].trim().length > 0;

                  return (
                    <Tabs.TabPane
                      tab={
                        <span>
                          {lang.toUpperCase()}
                          {hasText && (
                            <DeleteOutlined
                              style={{ marginLeft: 8, color: '#ff4d4f', cursor: 'pointer' }}
                              onClick={e => {
                                e.stopPropagation();
                                handleClearLang(lang);
                              }}
                              title={`Очистить ${lang.toUpperCase()}`}
                            />
                          )}
                        </span>
                      }
                      key={lang}
                      forceRender={true}
                    >
                      <Form.Item
                        label={`Текст после оплаты (${lang.toUpperCase()})`}
                        name={['translations', lang]}
                        rules={[
                          {
                            max: 1024,
                            message: `Максимальная длина текста 1024 символа`,
                          },
                        ]}
                      >
                        <Input.TextArea
                          rows={6}
                          maxLength={1024}
                          showCount
                          placeholder={`Введите текст для ${lang.toUpperCase()}...`}
                        />
                      </Form.Item>
                    </Tabs.TabPane>
                  );
                })}
              </Tabs>
            </Form>
          )}
        </Card>
      </ComponentRoleProtected>

      <div style={{ height: 1, background: '#f0f0f0', margin: '24px 0' }} />

      <div className={styles.header}>
        <Typography.Title level={4}>Пакеты</Typography.Title>
        <ComponentRoleProtected roles={[RoleEnum.admin, RoleEnum.companyContentManager]}>
          <Button type='primary' onClick={handleCreatePackageClick}>
            <PlusSquareOutlined />
          </Button>
        </ComponentRoleProtected>
      </div>

      <div className={styles.content}>
        <ComponentRoleProtected roles={[RoleEnum.admin, RoleEnum.companyContentManager]}>
          <div>
            {createMode && (
              <Form
                form={packageForm}
                validateTrigger='onBlur'
                layout='vertical'
                className={styles.form}
                onFinish={handlePackageSubmit}
              >
                <BuildOutlined />
                <Form.Item name='name' noStyle>
                  <Input
                    type='text'
                    name='name'
                    placeholder='Введите название пакета'
                    style={{ width: '75%' }}
                    onChange={e => setNewEntity(ev => ({ ...ev, name: e.target.value }))}
                  />
                </Form.Item>
                <Button htmlType='button' type='default' onClick={() => setCreateMode(false)}>
                  Отменить
                </Button>
                <Button htmlType='submit' type='primary' disabled={newEntity.name === ''}>
                  Создать
                </Button>
              </Form>
            )}
          </div>
        </ComponentRoleProtected>

        <div className={styles.cardsGrid}>
          {entityValues.map(c => (
            <Link key={c.id} to={`/commercial/companies/${companyId}/packages/${c.id}`}>
              <PackageTile {...c} />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CompanyPackagesTab;
